"use client"

import { createContext, useCallback, useContext, useEffect, useSyncExternalStore } from "react"

type Theme = "light" | "dark"

interface ThemeContextValue {
  theme: Theme
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "light",
  toggleTheme: () => {},
})

export function useTheme() {
  return useContext(ThemeContext)
}

// ─── Store externe : localStorage + préférence système ────────────────────────

function subscribeToTheme(callback: () => void): () => void {
  window.addEventListener("storage", callback)
  return () => window.removeEventListener("storage", callback)
}

function getThemeSnapshot(): Theme {
  const stored = localStorage.getItem("ljs-theme") as Theme | null
  if (stored === "light" || stored === "dark") return stored
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
}

// Rendu serveur : toujours "light" — identique au rendu initial client → pas de mismatch
function getServerSnapshot(): Theme {
  return "light"
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // useSyncExternalStore : pas de setState dans un effet → ESLint OK
  // côté client, re-lit localStorage après hydratation et re-rend si nécessaire
  const theme = useSyncExternalStore(subscribeToTheme, getThemeSnapshot, getServerSnapshot)

  // Synchronise la classe .dark sur <html> (mise à jour d'un système externe — pattern correct)
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark")
  }, [theme])

  const toggleTheme = useCallback(() => {
    const next: Theme = theme === "light" ? "dark" : "light"
    localStorage.setItem("ljs-theme", next)
    // Notifie les abonnés de useSyncExternalStore (multi-onglets inclus)
    window.dispatchEvent(new Event("storage"))
  }, [theme])

  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>
}
